import React from "react";
import { useTranslation } from "react-i18next";
import {
  RegisterReviewSection,
  RegisterReviewSectionContent,
  RegisterReviewSectionTitle,
} from "./Register.style";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import { TooltipInfo } from "../../../components/OtherUtilities/TooltipInfo/TooltipInfo";
import { isAffordableHoldUnit } from "./unitHelpers";

export const ConfirmUnitInformation = (props: any) => {
  const { t } = useTranslation();
  const { data, property, query } = props?.options || {};
  const Section = RegisterReviewSection;

  const SectionTitle = RegisterReviewSectionTitle;

  const SectionContent = RegisterReviewSectionContent;

  const isHoldUnit = isAffordableHoldUnit(data, property);

  // const pricingChanged = parseFloat(data?.rent) !== parseFloat(query?.pricing);
  const moveInChanged = moment(data?.moveInDate, "MM/DD/YYYY")
    .startOf("day")
    .isAfter(moment(query?.moveInDate, "MM/DD/YYYY"));
  const specialNoDateRange = data?.special && !data?.special?.DateRange;
  const originalRent = specialNoDateRange
    ? (
        parseFloat(data?.rent ?? 0) + parseFloat(data?.special?.Value ?? 0)
      ).toFixed(2)
    : null;
  return (
    <Section>
      <SectionTitle>{t("account.unitDetails.title")}</SectionTitle>
      <SectionContent>
        {/* Specials Section */}
        {data?.special && (
          <Box
            sx={{
              marginBottom: 3,
              padding: 2,
              borderRadius: 1,
              backgroundColor: "#fff8e1",
              border: "1px solid #f0e6b8",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "primary.main", marginBottom: 1 }}
            >
              {data.special.DescriptionHeader}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {data.special.DescriptionBody}
            </Typography>
          </Box>
        )}
        {/* Unit Basic Info Section */}
        {!isHoldUnit && (
          <Box sx={{ marginBottom: 3 }}>
            <Box display={"flex"} sx={{ marginBottom: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                {t("account.unitDetails.unitNumber")}: {data?.number}
              </Typography>
            </Box>
            <Box display={"flex"} sx={{ marginBottom: 2 }}>
              <Typography variant="body2">
                {!data?.bed || data?.bed === "0"
                  ? t("account.unitDetails.studio")
                  : `${data?.bed} ${t("account.unitDetails.beds")} ${
                      data?.bath
                    } ${t("account.unitDetails.baths")}`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Lease Terms Section */}
        {!isHoldUnit && (
          <Box
            sx={{
              marginBottom: 3,
              paddingTop: 2.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Box display={"flex"} sx={{ marginBottom: 2 }}>
              <Typography variant="body2">
                {t("account.unitDetails.term")}: {data?.term}{" "}
                {t("account.unitDetails.months")}
              </Typography>
            </Box>
            <Box display={"flex"} sx={{ marginBottom: 2 }}>
              <Typography variant="body2">
                {t("account.unitDetails.moveIn")}: {data?.moveInDate}
              </Typography>
              {!query?.leaseId && moveInChanged && (
                <TooltipInfo aligned={"right"}>
                  <Box width={"154px"}>
                    {t("account.unitDetails.latestMoveInPossibleFrom")}{" "}
                    {moment(data?.moveInDate, "MM/DD/YYYY").format(
                      "MM/DD/YYYY"
                    )}
                  </Box>
                </TooltipInfo>
              )}
            </Box>
          </Box>
        )}

        {/* Rent Section */}
        {!isHoldUnit && (
          <Box
            sx={{
              marginBottom: 3,
              paddingTop: 2.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Box display={"flex"} sx={{ marginBottom: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("account.unitDetails.rent")}:{" "}
                {specialNoDateRange ? (
                  <>
                    <Box
                      component={"span"}
                      sx={{
                        textDecoration: "line-through",
                        color: "text.disabled",
                        mr: 1,
                      }}
                    >
                      ${originalRent}
                    </Box>
                    <Box component={"span"}>
                      ${parseFloat(data?.rent ?? 0).toFixed(2)}
                    </Box>
                  </>
                ) : (
                  <>{`$${parseFloat(data?.rent ?? 0).toFixed(2)} ${t(
                    "account.unitDetails.perMonth"
                  )}`}</>
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Recurring Charges Section */}
        {data?.recurringCharges && data.recurringCharges.length > 0 && (
          <Box
            sx={{
              paddingTop: 2.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h3SemiBold"
              sx={{
                marginBottom: 2,
                color: "primary.main",
                fontSize: "15px",
              }}
            >
              {t("account.unitDetails.recurringCharges")}
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 1.5,
                padding: 2,
                border: "1px solid #e8e8e8",
              }}
            >
              {data.recurringCharges.map((charge: any, index: number) => (
                <Box
                  key={charge.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1.5,
                    borderBottom:
                      index < data.recurringCharges.length - 1
                        ? "1px solid #e0e0e0"
                        : "none",
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {charge.description ?? charge.transactionCategory.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      minWidth: "80px",
                      textAlign: "right",
                    }}
                  >
                    ${parseFloat(charge.amount).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* One-time Charges Section */}
        {(data?.deposit?.amount != null ||
          data?.applicationFee != null ||
          data?.adminFee != null) && (
          <Box
            sx={{
              marginBottom: 3,
              paddingTop: 2.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h3SemiBold"
              sx={{
                marginBottom: 2,
                color: "primary.main",
                fontSize: "15px",
              }}
            >
              {t("account.unitDetails.oneTimeCharges")}
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 1.5,
                padding: 2,
                border: "1px solid #e8e8e8",
              }}
            >
              {data?.deposit?.amount != null ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1.5,
                    borderBottom:
                      data?.applicationFee != null || data?.adminFee != null
                        ? "1px solid #e0e0e0"
                        : "none",
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t("account.unitDetails.deposit")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      minWidth: "80px",
                      textAlign: "right",
                    }}
                  >
                    ${parseFloat(data.deposit.amount).toFixed(2)}
                  </Typography>
                </Box>
              ) : null}
              {data?.applicationFee != null ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1.5,
                    borderBottom:
                      data?.adminFee != null ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                    {t("account.leaseApplication.applicationFee")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      minWidth: "80px",
                      textAlign: "right",
                    }}
                  >
                    ${parseFloat(data.applicationFee).toFixed(2)}
                  </Typography>
                </Box>
              ) : null}
              {data?.adminFee != null ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                    {t("account.leaseApplication.unitFee")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      minWidth: "80px",
                      textAlign: "right",
                    }}
                  >
                    ${parseFloat(data.adminFee).toFixed(2)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        )}

        {/* Disclaimer Section */}
        {(data?.deposit?.amount != null ||
          data?.applicationFee != null ||
          data?.adminFee != null) && (
          <Typography
            variant="body2"
            sx={{
              marginTop: 2,
              color: "text.secondary",
              fontSize: "0.75rem",
              lineHeight: 1.5,
            }}
          >
            * {t("account.unitDetails.feesDisclaimer")}
          </Typography>
        )}
      </SectionContent>
    </Section>
  );
};
